import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Clock, CheckCircle2, AlertOctagon, CalendarClock, Undo2, Banknote } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Kpi, Panel, useSort, SortTh, TH_ROW, TD, inr, inrCompact, num, fmtDate, Pill, FieldSelect, TableFilter, textMatch } from "../aggKit";

const ST = {
  pending: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  processed: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  overdue: "bg-rose-600 text-white border-rose-600",
};
const TYPE_LABEL = { msme: "MSME · COD release", franchise: "Franchise · revenue share" };

export default function PaymentCycles({ tick }) {
  const [d, setD] = useState(null);
  const [t, setT] = useState(0);
  const [ptype, setPtype] = useState("");
  const [status, setStatus] = useState("");
  const [sched, setSched] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const load = useCallback(() => {
    api.get("/agg/fin/payouts", { params: { payee_type: ptype || undefined } }).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [ptype]);
  useEffect(() => { load(); setSel([]); }, [load, tick, t]);

  const items = useMemo(() => (d?.items || []).filter((p) => (!status || p.status_eff === status) && (!sched || p.schedule === sched) &&
    textMatch(p, q, ["cycle_no", "payee_name", "utr"])), [d, status, sched, q]);
  const sort = useSort(items, "");
  const payees = useSort((d?.payees || []).filter((p) => textMatch(p, q, ["payee_name"])), "overdue");
  const payable = (p) => p.status_eff !== "processed" && !p.accruing;
  const selAmt = items.filter((p) => sel.includes(p.id)).reduce((a, p) => a + p.amount, 0);

  const process = async (ids) => {
    const utr = window.prompt(`Release ${ids.length} payout(s)? Enter bank batch UTR (leave blank to auto-generate):`, "");
    if (utr === null) return;
    try { const r = await api.post("/agg/fin/payouts/process", { ids, utr }); toast.success(`Processed ${r.data.processed} · ${inr(r.data.amount)}`); setT((x) => x + 1); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const revert = async (p) => {
    if (!window.confirm(`Mark ${p.cycle_no} as not paid?`)) return;
    try { await api.post(`/agg/fin/payouts/${p.id}/revert`); toast.success("Reverted to pending"); setT((x) => x + 1); } catch (e) { toast.error(apiErr(e)); }
  };
  const setSchedule = async (p, schedule) => {
    try { await api.patch(`/agg/fin/payees/${p.payee_type}/${p.payee_id}`, { schedule }); toast.success(`${p.payee_name} → ${schedule.toUpperCase()}`); setT((x) => x + 1); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!d) return <div className="text-sm text-muted-foreground p-6">Loading payment cycles…</div>;
  const s = d.summary;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Kpi testid="pc-kpi-pending" label="Pending" icon={Clock} accent="text-amber-500" value={inrCompact(s.pending.amount)} sub={`${num(s.pending.count)} cycles not yet due`} />
        <Kpi testid="pc-kpi-overdue" label="Overdue" icon={AlertOctagon} accent="text-rose-500" value={inrCompact(s.overdue.amount)} sub={`${num(s.overdue.count)} cycles past due date`} />
        <Kpi testid="pc-kpi-processed" label="Processed" icon={CheckCircle2} accent="text-emerald-500" value={inrCompact(s.processed.amount)} sub={`${num(s.processed.count)} cycles paid out`} />
        <Kpi testid="pc-kpi-next" label="Next payout due" icon={CalendarClock} accent="text-sky-500" value={d.next_due ? fmtDate(d.next_due.due_date) : "—"} sub={d.next_due ? `${d.next_due.payee_name} · ${inr(d.next_due.amount)}` : "nothing scheduled"} />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <FieldSelect testid="pc-type" value={ptype} onChange={setPtype} className="h-8 text-xs"><option value="">All payees</option><option value="msme">MSMEs (COD release)</option><option value="franchise">Franchise partners (revenue share)</option></FieldSelect>
        <FieldSelect testid="pc-status" value={status} onChange={setStatus} className="h-8 text-xs"><option value="">All statuses</option><option value="pending">Pending</option><option value="overdue">Overdue</option><option value="processed">Processed</option></FieldSelect>
        <FieldSelect testid="pc-sched" value={sched} onChange={setSched} className="h-8 text-xs"><option value="">All schedules</option>{d.schedules.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}</FieldSelect>
        <TableFilter testid="pc-search" value={q} onChange={setQ} placeholder="Filter payee, cycle, UTR…" />
        <div className="flex-1" />
        {sel.length > 0 && <button data-testid="pc-process-selected" onClick={() => process(sel)} className="h-8 px-3 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wide inline-flex items-center gap-1.5"><Banknote className="h-3.5 w-3.5" /> Process {sel.length} · {inrCompact(selAmt)}</button>}
      </div>

      <Panel testid="pc-table" title="Payment cycle log" subtitle="Weekly cycles. Net-3 / Net-7 / Weekly sets how many days after the cycle ends the payout is due." bodyClass="overflow-x-auto max-h-[560px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className={`${TH_ROW} sticky top-0 z-10`}>
            <tr>
              <th className="px-3 py-2 w-8"><input type="checkbox" data-testid="pc-select-all" checked={sel.length > 0 && sel.length === items.filter(payable).length}
                onChange={(e) => setSel(e.target.checked ? items.filter(payable).map((p) => p.id) : [])} /></th>
              <SortTh k="cycle_no" label="Cycle" sort={sort} />
              <SortTh k="payee_name" label="Payee" sort={sort} />
              <SortTh k="schedule" label="Schedule" sort={sort} />
              <SortTh k="period_start" label="Period" sort={sort} />
              <SortTh k="due_date" label="Due" sort={sort} />
              <SortTh k="parcels" label="Parcels" sort={sort} align="right" />
              <SortTh k="amount" label="Amount" sort={sort} align="right" />
              <SortTh k="status_eff" label="Status" sort={sort} />
              <th className="px-3 py-2 text-left">Settlement</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sort.sorted.length === 0 && <tr><td colSpan={11} className="p-6 text-center text-muted-foreground">No cycles match.</td></tr>}
            {sort.sorted.map((p) => (
              <tr key={p.id} className={`hover:bg-muted/40 ${p.status_eff === "overdue" ? "bg-rose-500/5" : ""}`} data-testid={`pc-row-${p.id}`}>
                <td className="px-3 py-2">{payable(p) && <input type="checkbox" checked={sel.includes(p.id)} onChange={(e) => setSel((x) => (e.target.checked ? [...x, p.id] : x.filter((i) => i !== p.id)))} />}</td>
                <td className={`${TD} font-mono-num font-bold`}>{p.cycle_no}</td>
                <td className={TD}><div className="font-semibold">{p.payee_name}</div><div className="text-[10px] text-muted-foreground">{TYPE_LABEL[p.payee_type]}</div></td>
                <td className={TD}><Pill className="bg-muted text-foreground border-border">{p.schedule_label}</Pill></td>
                <td className={TD}>{fmtDate(p.period_start)} – {fmtDate(p.period_end)}</td>
                <td className={`${TD} ${p.status_eff === "overdue" ? "text-rose-600 dark:text-rose-400 font-bold" : ""}`}>{fmtDate(p.due_date)}{p.days_overdue > 0 && <div className="text-[10px]">{p.days_overdue}d overdue</div>}</td>
                <td className={`${TD} text-right font-mono-num`}>{num(p.parcels)}</td>
                <td className={`${TD} text-right font-mono-num font-bold`}>{inr(p.amount, 2)}</td>
                <td className={TD}><Pill className={ST[p.status_eff]}>{p.status_eff}</Pill>{p.accruing && <div className="text-[10px] text-muted-foreground mt-0.5">cycle still running</div>}</td>
                <td className={`${TD} text-[11px]`}>{p.utr ? <><div className="font-mono-num">{p.utr}</div><div className="text-muted-foreground">{fmtDate(p.processed_at, true)}</div></> : <span className="text-muted-foreground">{p.bank || "—"}</span>}</td>
                <td className={TD}>
                  {payable(p) ? <button onClick={() => process([p.id])} data-testid={`pc-process-${p.id}`} className="h-6 px-2 bg-primary text-primary-foreground text-[10px] font-bold uppercase">Process</button>
                    : p.status_eff === "processed" ? <button title="Revert" onClick={() => revert(p)} data-testid={`pc-revert-${p.id}`} className="h-6 w-6 grid place-items-center border border-border hover:bg-muted"><Undo2 className="h-3 w-3" /></button> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel testid="pc-payees" title="Payees & payout schedules" subtitle="Changing a schedule re-dates that payee's unpaid cycles" bodyClass="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className={TH_ROW}>
            <tr>
              <SortTh k="payee_name" label="Payee" sort={payees} />
              <SortTh k="payee_type" label="Type" sort={payees} />
              <th className="px-3 py-2 text-left">Schedule</th>
              <SortTh k="cycles" label="Cycles" sort={payees} align="right" />
              <SortTh k="overdue" label="Overdue" sort={payees} align="right" />
              <SortTh k="pending" label="Pending" sort={payees} align="right" />
              <SortTh k="processed" label="Paid out" sort={payees} align="right" />
              <SortTh k="on_time_pct" label="Paid on time" sort={payees} align="right" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {payees.sorted.map((p) => (
              <tr key={p.payee_id} className="hover:bg-muted/40" data-testid={`payee-row-${p.payee_id}`}>
                <td className={`${TD} font-semibold`}>{p.payee_name}</td>
                <td className={TD}>{TYPE_LABEL[p.payee_type]}</td>
                <td className={TD}><FieldSelect testid={`payee-sched-${p.payee_id}`} value={p.schedule} onChange={(v) => setSchedule(p, v)} className="h-7 text-xs">{d.schedules.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}</FieldSelect></td>
                <td className={`${TD} text-right font-mono-num`}>{p.cycles}</td>
                <td className={`${TD} text-right font-mono-num ${p.overdue > 0 ? "text-rose-600 dark:text-rose-400 font-bold" : ""}`}>{p.overdue ? inr(p.overdue) : "—"}</td>
                <td className={`${TD} text-right font-mono-num`}>{p.pending ? inr(p.pending) : "—"}</td>
                <td className={`${TD} text-right font-mono-num`}>{inrCompact(p.processed)}</td>
                <td className={`${TD} text-right font-mono-num`}>{p.on_time_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
